import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, ListItemIcon, Radio, Stack, Typography } from '@mui/material'
import T from 'T'
import HHConfirmDialog from 'components/common/HHConfirmDialog'
import { useRemovePaymentMethodMutation } from 'api/payment-gateway/paymentGateway'
import { handleError } from 'utils/error'
import { shallowEqual, useSelector } from 'react-redux'
import { get } from 'lodash'
import PaymentMethodListItem from 'components/common/PaymentMethodListItem'
import { toast } from 'react-toastify'

const SelectPaymentMethodListItem = ({ method, selectedPaymentMethodId, onChange }) => {
  const { accountType, expiry } = method
  const businessId = useSelector(state => get(state, 'LoginPersist.selectedLoginDetails.businessId'), shallowEqual)
  const accountId = useSelector(state => get(state, 'LoginPersist.selectedLoginDetails.accountId'), shallowEqual)
  const isNotECHK = accountType !== 'ECHK'
  const expiryFormatted = expiry && expiry.length >= 4 ? `${expiry.substr(0, 2)}/${expiry.substr(2)}` : ''
  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState({ isOpen: false, card: null })
  const [removePaymentMethod, { isLoading: isRemoving }] = useRemovePaymentMethodMutation()

  const isSelected = selectedPaymentMethodId === method.id

  const openDeleteDialog = () => {
    setConfirmDeleteDialog({ isOpen: true })
  }

  const handleConfirmDeleteDialog = async () => {
    try {
      await removePaymentMethod({
        businessId,
        payload: {
          accountId,
          ...method,
        },
      }).unwrap()
      toast.success(T.PAYMENT_METHOD_DELETED_SUCCESS)
    } catch (error) {
      handleError(error)
    } finally {
      setConfirmDeleteDialog({ isOpen: false, card: null })
    }
  }

  const handleCloseConfirmDeleteDialog = () => {
    setConfirmDeleteDialog({ isOpen: false })
  }

  return (
    <>
      <Stack direction="column">
        <Stack direction="row" justifyContent="flex-end">
          <Button
            color="error"
            onClick={openDeleteDialog}
            disabled={isRemoving}
            sx={{
              '&:focus-visible': theme => ({
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 2,
              }),
            }}
          >
            {T.REMOVE}
          </Button>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            px: 1,
            borderRadius: 1,
            cursor: 'pointer',
            '&:focus-visible': theme => ({
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            }),
          }}
          role="radio"
          aria-checked={isSelected}
          tabIndex={0}
          onClick={onChange}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onChange(e)
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 'unset' }}>
            <Radio onChange={onChange} checked={isSelected} />
          </ListItemIcon>
          <Box flex={1}>
            <PaymentMethodListItem method={method} nickNamePreference />
          </Box>
          <Box flex={1}>{isNotECHK && expiryFormatted && <Typography align="right">{`Expires ${expiryFormatted}`}</Typography>}</Box>
        </Stack>
      </Stack>
      <HHConfirmDialog
        isOpen={confirmDeleteDialog.isOpen}
        DialogProps={{ fullWidth: true, maxWidth: 'xs' }}
        confirmTitle={T.REMOVE_PAYMENT_METHOD_TITLE}
        confirmDescription={
          <Stack spacing={1.5}>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1 }}>
              <PaymentMethodListItem method={method} nickNamePreference />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {T.REMOVE_PAYMENT_METHOD_DESCRIPTION}
            </Typography>
          </Stack>
        }
        onConfirm={handleConfirmDeleteDialog}
        onClose={handleCloseConfirmDeleteDialog}
        confirmButtonTitle={T.REMOVE}
        ConfirmButtonProps={{ loading: isRemoving, disabled: isRemoving }}
      />
    </>
  )
}

SelectPaymentMethodListItem.propTypes = {
  method: PropTypes.object,
  selectedPaymentMethodId: PropTypes.string,
  onChange: PropTypes.func,
}

export default SelectPaymentMethodListItem
